// Runtime config remoto — lido NO BOOT do app TV pra descobrir lista
// de URLs de API (failover), kill switch, banner global etc.
//
// Por que existe: app TV em Tizen/webOS NÃO consegue trocar URL hardcoded
// sem republicar (loja demora dias). Com config remoto + failover:
//   - Se URL primária da API cai, cliente rotaciona pra próxima da lista
//   - Em emergência, killSwitch desliga o app globalmente
//   - Banner exibe avisos (manutenção, instabilidade)
//
// Fonte do JSON (evolução):
//   - Piloto: VITE_RUNTIME_CONFIG_URL vazio → cai no fallback `${VITE_API_BASE_URL}/v1/config/runtime`
//     (mesma API que vai falhar se domínio principal cair — protege contra
//     bugs/configs, não contra takedown de domínio)
//   - Produção (após comprar swipetvcfg.com): VITE_RUNTIME_CONFIG_URL=
//     https://cfg.swipetvcfg.com/runtime.json (R2 estático, domínio
//     separado, praticamente imune a takedown)
//
// Detalhes em docs/operacional/dominios.md.

import { API_ORIGIN_LITERAL_FALLBACK, API_ORIGIN_SEEDS } from './api-origin-fallback'
import { makeNetDeadline } from './lib/deadline'

export interface RuntimeConfig {
  version: number
  lastUpdated: string
  api: {
    endpoints: string[]
    healthPath: string
    timeoutMs: number
  }
  minAppVersion: string
  messageBanner: string | null
  killSwitch: boolean
  // URL base do site B2C (swipetvplayer.com) — fonte de verdade é o env
  // `SITE_URL` da API, exposta via `getSiteUrl()` no GET /v1/config/runtime
  // (paridade manual com `interface RuntimeConfig` em apps/api/src/routes/
  // config.ts). Usada no QR de pareamento e nos links de compra/planos.
  //
  // OPCIONAL no tipo do cliente de propósito: config remoto antigo (cache de
  // até 6h, ou payload de API ainda não atualizada) NÃO tem o campo, e isso
  // NÃO deve invalidar o config (ver isValidConfig). O consumidor cai no
  // fallback síncrono de build (VITE_SITE_URL → literal), então o QR nunca
  // fica undefined no 1º paint.
  siteUrl?: string
}

const CACHE_KEY = 'runtime-config-v1'

// TTL do cache — DOIS eixos, de propósito (P3, §5 do plano). O cache só é lido
// quando a REDE FALHA (loadRuntimeConfig tenta rede primeiro, sempre); um boot
// bem-sucedido sobrescreve e regrava. Então a velocidade de propagação de mudança
// legítima é governada pelo boot, NÃO pelo TTL — alongar o TTL não atrasa nada no
// caminho feliz. O TTL governa só "por quanto tempo confio em dado velho quando
// estou SEM REDE".
//
//   - CACHE_MAX_AGE_MS (30 dias): teto de `endpoints`/`siteUrl`. Endpoint velho é
//     inofensivo — o failover trata —, e 30 dias cobre bloqueio prolongado e
//     aparelho semanas desligado. 6h (o valor antigo) era a diferença entre um
//     bloqueio de fim de semana ser transparente ou derrubar a base inteira.
//   - VOLATILE_MAX_AGE_MS (24h): teto dos campos VOLÁTEIS (`killSwitch`,
//     `messageBanner`). Um `killSwitch: true` cacheado e depois revertido do nosso
//     lado ficaria PRESO por 30 dias num aparelho sem rede — o app seguiria
//     desligado depois de já termos consertado. Com o TTL curto isso se autocurava;
//     com 30 dias, não. Daí a regra em readCache: cache com mais de 24h preserva
//     endpoints/siteUrl mas força killSwitch=false e messageBanner=null. Endpoint
//     velho é seguro; kill switch velho é fatal.
const CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000 // 30 dias
const VOLATILE_MAX_AGE_MS = 24 * 60 * 60 * 1000   // 24h

const PER_SOURCE_TIMEOUT_MS = 1500       // timeout POR fonte de bootstrap. Antes
                                         // era um fetch único de 3s; agora são
                                         // várias fontes tentadas em ordem, então
                                         // cada degrau precisa ceder rápido pra o
                                         // próximo. Roda em background (main.tsx),
                                         // fora do caminho do 1º paint — o custo
                                         // acumulado não bloqueia a tela.

/** Dedup preservando ordem, ES5-safe (sem Set — o piso Chr38 tem Set parcial e
 *  este código roda no boot, antes de qualquer polyfill de runtime garantir o
 *  construtor com iterable). Filtra também vazios/whitespace. */
function dedupOrigins(list: readonly string[]): string[] {
  const out: string[] = []
  for (let i = 0; i < list.length; i++) {
    const v = typeof list[i] === 'string' ? list[i]!.trim() : ''
    if (v !== '' && out.indexOf(v) === -1) out.push(v)
  }
  return out
}

let active: RuntimeConfig | null = null
let activeApiIdx = 0

// ===== Public API =====

/**
 * Carrega o runtime config. É uma CORRENTE de fontes, cada degrau ADITIVO:
 * falhar num degrau nunca impede o seguinte, e o último degrau é literal e não
 * pode falhar. Ver a §4 de docs/plan/origem-da-api-sem-rebuild.md.
 *
 *   1. cada URL de `bootstrapUrls`, EM ORDEM, com timeout curto por degrau —
 *      1ª que responde JSON válido vence (salva cache). Tipicamente:
 *        [ domínio de config estático?, ${api}/v1/config/runtime (por semente) ]
 *      A API NUNCA é removida da lista — mesmo com domínio de config setado, ela
 *      entra como degrau seguinte (é o invariante que evita "ou/ou").
 *   2. cache local (localStorage) — dado que sobrevive a bloqueio/offline.
 *   3. `makeFallback(fallbackApiUrls)` — sementes embutidas no build, literais.
 *
 * SEQUENCIAL, não corrida: `Promise.any` não existe no piso Chr38, e numa corrida
 * o degrau mais RÁPIDO venceria — um espelho estático desatualizado bateria a API
 * fresca. Desempate por `version`: uma fonte que responde com `version` MENOR que
 * o já cacheado é IGNORADA (não rebaixa config novo por espelho velho).
 *
 * Sempre resolve. Nunca lança — pior caso retorna as sementes.
 * Chamar UMA VEZ no boot, antes de qualquer call à API.
 *
 * @param bootstrapUrls   fontes de config, em ordem de preferência (a corrente)
 * @param fallbackApiUrls sementes de origem da API pro piso (≥1, absolutas)
 */
export async function loadRuntimeConfig(
  bootstrapUrls: readonly string[],
  fallbackApiUrls: readonly string[],
): Promise<RuntimeConfig> {
  // Baseline de version pro desempate — lido UMA vez. Também é o degrau de cache
  // (2) reaproveitado no fim, então já vem com o decaimento aplicado (readCache).
  const cachedBaseline = readCache()

  // 1. Corrente de fontes de rede, cada uma com timeout próprio.
  for (let i = 0; i < bootstrapUrls.length; i++) {
    const cfg = await fetchConfigOnce(bootstrapUrls[i]!)
    if (!cfg) continue // degrau caiu (rede/timeout/HTTP/JSON) — tenta o próximo
    // Desempate: fonte que rebaixa a version do cache é descartada. Segue pro
    // próximo degrau (ex.: a API fresca) em vez de aceitar espelho velho.
    if (cachedBaseline && cfg.version < cachedBaseline.version) continue
    active = cfg
    activeApiIdx = 0
    saveCache(cfg)
    return cfg
  }

  // 2. Cache local (já decaído por readCache — killSwitch velho não sobrevive).
  if (cachedBaseline) {
    active = cachedBaseline
    activeApiIdx = 0
    return cachedBaseline
  }

  // 3. Piso: sementes embutidas no build (literais, nunca falham).
  active = makeFallback(fallbackApiUrls)
  activeApiIdx = 0
  return active
}

/**
 * Cache-buster na URL. O `cache: 'no-store'` do `fetch` NÃO é suficiente no
 * fleet: `RequestInit.cache` só existe a partir do Chr64, e de Chr42 a Chr63
 * quem atende é o `fetch` NATIVO (o `whatwg-fetch` só se instala sob
 * `if (!g.fetch)`), que descarta o membro desconhecido em silêncio. Nessa faixa
 * — webOS 4.x/Chr53, Tizen 2017-2018 — o `Cache-Control: public, max-age=300`
 * que `GET /v1/config/runtime` devolve (`apps/api/src/routes/config.ts:245`)
 * volta a valer e a resposta pode vir do cache HTTP do WebView.
 *
 * Query param resolve em TODO motor porque muda a CHAVE de cache, sem depender
 * de nenhuma opção de `RequestInit`. Usa `_=`, o mesmo nome que o `whatwg-fetch`
 * usa no piso Chr38 (`fetch.js:398`): quando o polyfill atende, ele encontra o
 * parâmetro que já existe e o REESCREVE em vez de anexar um segundo.
 *
 * Pura de propósito (testável sem rede).
 */
export function withCacheBuster(url: string): string {
  return url + (url.indexOf('?') === -1 ? '?' : '&') + '_=' + String(Date.now())
}

/** Busca UMA fonte de config com prazo próprio. Devolve o config válido ou
 *  null (rede caída, prazo estourado, HTTP != 2xx, JSON inválido). Nunca lança.
 *
 *  O prazo é um RACE (`makeNetDeadline`), não um `setTimeout(abort)`: na faixa
 *  Chr42-63 o `signal` é tão ignorado quanto o `cache`, então o abort sozinho
 *  NUNCA libera este `await` — e a corrente de fontes do `loadRuntimeConfig`
 *  nunca alcançava a fonte seguinte. Abortar segue valendo (solta o socket onde
 *  funciona), mas quem garante a terminação é o `net.expired`. */
async function fetchConfigOnce(url: string): Promise<RuntimeConfig | null> {
  const net = makeNetDeadline(PER_SOURCE_TIMEOUT_MS, `runtime-config ${url}`)
  try {
    const base: RequestInit = { cache: 'no-store' }
    // `exactOptionalPropertyTypes`: só inclui `signal` quando a global existe.
    const init: RequestInit = net.signal ? { ...base, signal: net.signal } : base
    const r = await Promise.race([fetch(withCacheBuster(url), init), net.expired])
    if (!r.ok) return null
    const cfg = (await Promise.race([r.json(), net.expired])) as RuntimeConfig
    return isValidConfig(cfg) ? cfg : null
  } catch {
    return null
  } finally {
    net.clear()
  }
}

/** Estado atual carregado. Lança se loadRuntimeConfig não foi chamado. */
export function getRuntimeConfig(): RuntimeConfig {
  if (!active) {
    throw new Error('runtime-config: loadRuntimeConfig() must be called before getRuntimeConfig()')
  }
  return active
}

/** URL da API atualmente preferida (índice 0 do array endpoints, ou backup). */
export function getActiveApiUrl(): string {
  if (!active) {
    throw new Error('runtime-config: not loaded')
  }
  return active.api.endpoints[activeApiIdx]!
}

// Fallback síncrono de build da URL do site (camadas: VITE_SITE_URL → literal).
// O literal espelha SITE_URL_FALLBACK do backend (apps/api/src/lib/site-url.ts),
// sem barra final — mantém o QR/links válidos mesmo sem env nem runtime-config.
const SITE_URL_BUILD_FALLBACK =
  (import.meta.env['VITE_SITE_URL'] as string | undefined) ?? 'https://swipetvplayer.com'

// P3 (perf/tv-hero-coldstart): fallback de build da URL da API — a origem
// PRIMÁRIA. Alimenta `API_SEED_ORIGINS` (a lista completa que main.tsx importa pra
// montar as fontes de bootstrap e que a semente síncrona de `active` consome no
// fim do módulo) — um valor só na cabeça da lista, sem chance de divergir.
//
// O LITERAL É REDE DE SEGURANÇA, não configuração. Espelha exatamente o que
// SITE_URL_BUILD_FALLBACK faz seis linhas acima (e o default do build-arg
// `ARG VITE_API_BASE_URL=https://api.swipetvplayer.com` em apps/tv/Dockerfile).
// A assimetria entre os dois campos — site com literal, API caindo em '' — era a
// raiz do incidente abaixo; aqui ela acaba.
//
// INCIDENTE QUE ORIGINOU O LITERAL (.ipk 2.24.2, webOS): o pacote foi buildado sem
// VITE_API_BASE_URL. Esta const virou '', então o `bootstrapUrl` do main.tsx virou
// a URL ROOT-RELATIVA '/v1/config/runtime'. Num app empacotado o documento é
// `file://`, e root-relativo ali resolve pra `file:///v1/config/runtime` — que não
// existe. O app não tinha COMO descobrir a API: sem bootstrap não há endpoints, sem
// endpoints não há catálogo, login nem pareamento. Aparelho com sessão salva
// mascarou a falha por até 6h (TTL do cache do runtime-config), então o bug é
// INVISÍVEL em quem já usava e FATAL em instalação limpa. Numa loja da LG, onde
// toda instalação é limpa, o pacote nasceria morto pra 100% dos usuários.
//
// `||` e NÃO `??` de propósito: o modo de falha real é string VAZIA (env declarada
// sem valor, `VITE_API_BASE_URL=`, ou build-arg passado vazio), e `??` só pega
// null/undefined — deixaria o '' passar e reabriria o incidente inteiro.
//
// Este literal é o PISO, não a licença pra não configurar: o build de TV aborta sem
// a env (guard `tv-api-origin-guard` em vite.config.ts) e o empacotamento imprime a
// origem que achou (scripts/compat/check-api-origin.mjs), pra que uma origem
// absoluta porém ERRADA (staging, host aposentado) apareça no log em vez de passar
// calada. Sozinho, o literal mascararia erro de configuração.
export const API_BASE_BUILD_FALLBACK =
  (import.meta.env['VITE_API_BASE_URL'] as string | undefined) || API_ORIGIN_LITERAL_FALLBACK

/**
 * Lista COMPLETA de origens de API embutidas, em ordem de preferência e sem
 * duplicatas. A PRIMÁRIA (`API_BASE_BUILD_FALLBACK` — env de build quando veio,
 * senão o literal) vem primeiro, seguida das sementes de resiliência
 * (`API_ORIGIN_SEEDS`). O dedup remove a colisão comum: sem env de build, a
 * primária JÁ é a 1ª semente, então a lista fica com um elemento só até o 2º
 * domínio existir.
 *
 * É a fonte única consumida por:
 *   - `makeFallback` (semente síncrona + piso do loadRuntimeConfig): vira os
 *     `api.endpoints`, então o failover em runtime (rotateApiUrl no api.ts) tem
 *     PRA ONDE rotacionar mesmo sem config remoto — hoje só quando houver 2+
 *     origens, mas sem mudar código quando isso acontecer;
 *   - `main.tsx`: cada origem vira uma fonte de bootstrap `${origem}/v1/config/
 *     runtime`, garantindo que a API entre na corrente de descoberta.
 */
export const API_SEED_ORIGINS: readonly string[] = dedupOrigins([
  API_BASE_BUILD_FALLBACK,
  ...API_ORIGIN_SEEDS,
])

/**
 * URL base do site B2C servida pelo runtime-config. Se o config ainda não foi
 * carregado, ou veio de cache antigo sem o campo `siteUrl`, cai no fallback
 * síncrono de build (VITE_SITE_URL → literal). NUNCA retorna undefined — o QR
 * de pareamento e os links de compra precisam de valor no 1º paint.
 */
export function getActiveSiteUrl(): string {
  return active?.siteUrl ?? SITE_URL_BUILD_FALLBACK
}

/**
 * Marca a URL atual como falha e rotaciona pra próxima.
 * Retorna true se ainda há próxima; false se esgotou todas (caller deve
 * propagar erro pra UI).
 */
export function rotateApiUrl(): boolean {
  if (!active) return false
  if (activeApiIdx >= active.api.endpoints.length - 1) return false
  activeApiIdx++
  console.warn(
    `[runtime-config] rotacionando API: ${active.api.endpoints[activeApiIdx - 1]} → ${active.api.endpoints[activeApiIdx]}`,
  )
  return true
}

/** Volta o pointer pra URL primária (após sucesso, dá nova chance à primária). */
export function resetApiUrl(): void {
  activeApiIdx = 0
}

// ===== Internals =====

function isValidConfig(cfg: unknown): cfg is RuntimeConfig {
  if (!cfg || typeof cfg !== 'object') return false
  const c = cfg as Record<string, unknown>
  if (typeof c['version'] !== 'number') return false
  const api = c['api'] as Record<string, unknown> | undefined
  if (!api || !Array.isArray(api['endpoints']) || api['endpoints'].length === 0) return false
  if (api['endpoints'].some((u) => typeof u !== 'string' || !u.startsWith('http'))) return false
  return true
}

function saveCache(cfg: RuntimeConfig): void {
  try {
    const payload = { savedAt: Date.now(), cfg }
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload))
  } catch {
    // quota cheia, privado, etc — silencioso (cache é otimização, não required)
  }
}

function readCache(): RuntimeConfig | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { savedAt: number; cfg: RuntimeConfig }
    const age = Date.now() - parsed.savedAt
    // Além do teto duro: cache descartado por inteiro (endpoint velho até serve,
    // mas 30 dias é o limite do que faz sentido confiar offline).
    if (age > CACHE_MAX_AGE_MS) return null
    if (!isValidConfig(parsed.cfg)) return null
    // Entre 24h e 30 dias: endpoints/siteUrl sobrevivem (o failover trata endpoint
    // velho), mas os campos voláteis DECAEM — um killSwitch/banner de emergência
    // que já revertemos não pode ficar preso num aparelho sem rede. Objeto NOVO,
    // sem mutar o cacheado.
    if (age > VOLATILE_MAX_AGE_MS) {
      return { ...parsed.cfg, killSwitch: false, messageBanner: null }
    }
    return parsed.cfg
  } catch {
    return null
  }
}

function makeFallback(apiUrls: readonly string[]): RuntimeConfig {
  // Guarda anti-vazio: se por algum motivo a lista chegar vazia (dedup zerou
  // tudo), o piso do piso é o literal — um endpoint sempre existe, senão
  // getActiveApiUrl() lançaria e o boot cairia.
  const endpoints = dedupOrigins(apiUrls)
  return {
    version: 1,
    lastUpdated: new Date(0).toISOString(),
    api: {
      endpoints: endpoints.length > 0 ? endpoints : [API_ORIGIN_LITERAL_FALLBACK],
      healthPath: '/health',
      timeoutMs: 8000,
    },
    minAppVersion: '1.0.0',
    messageBanner: null,
    killSwitch: false,
    // Fallback de build (VITE_SITE_URL → literal) — mesmo valor que
    // getActiveSiteUrl() usaria sem config; aqui só deixa o objeto completo.
    siteUrl: SITE_URL_BUILD_FALLBACK,
  }
}

// ===== Semente síncrona (P3 — perf/tv-hero-coldstart) =====
//
// `getActiveApiUrl`/`getActiveSiteUrl` LANÇAM (ou caíam no fallback) quando
// `active` é null. Antes, o boot AGUARDAVA `loadRuntimeConfig` — cujo fetch
// no-store bloqueia até 3s ANTES de cair no fallback síncrono — só pra ter um
// `active` válido no 1º paint. Aqui semeamos `active` com o MESMO fallback de
// build (makeFallback) de forma síncrona no load do módulo: o boot monta na
// hora e `loadRuntimeConfig` roda depois EM BACKGROUND (main.tsx), sobrescrevendo
// `active` reativamente quando o config remoto volta (endpoints de failover,
// killSwitch, banner, minAppVersion — aplicados ao voltar, não antes do paint).
//
// Roda AQUI, no fim do módulo, porque makeFallback depende de
// `SITE_URL_BUILD_FALLBACK` (const acima) — chamá-la no ponto de declaração de
// `active` (topo) cairia na TDZ desse const. `__resetForTests()` volta `active`
// pra null, então a suíte segue exercitando o estado "não carregado".
active = makeFallback(API_SEED_ORIGINS)
activeApiIdx = 0

// ===== Test helpers (only) =====

/** Reset interno — USAR SÓ EM TESTES. */
export function __resetForTests(): void {
  active = null
  activeApiIdx = 0
  try {
    localStorage.removeItem(CACHE_KEY)
  } catch {
    /* ignore */
  }
}
